import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtEur, fmtDate } from '../../lib/utils';
import { toast } from '../../stores/toast.store';
import { celebrate } from '../../stores/moment.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import type { IgnoreReasonKey, MatchKind, PayMethod, UscitaSource } from './adminApi';

/**
 * Azioni condivise dell'Amministrazione (tab, stream Oggi, modali).
 *
 * Stesso pattern di useDocActions: ogni mutation invalida ['admin'] (tutte le
 * viste leggono gli stessi aggregati: overview, incassi, uscite, movimenti),
 * gestisce toast/undo/celebrate; i consumer chiamano solo `.mutate(...)` e
 * usano `.isPending` per il disable.
 */

export interface MarkPaidArgs {
  paymentId: string;
  amount: number;
  label?: string;          // "Fattura 2026/12 · La Rosa" per il toast
  paidDate: string;        // YYYY-MM-DD
  method: PayMethod;
  accountId?: string | null;
  bankTransactionId?: string | null;
  /** Perché la si segna qui: finisce in Banca, fra i pagamenti da comunicare al commercialista. */
  localPaidNote?: string | null;
  /** Chiamata dopo un Annulla riuscito (lo stream Oggi rimette la riga al suo posto). */
  onUndone?: () => void;
}

export interface PayArgs {
  source: UscitaSource;
  id: string;
  amount: number;
  label?: string;
  paidDate: string;
  method: PayMethod;
  accountId?: string | null;
  bankTransactionId?: string | null;
  /** Ricorrenti: quale occorrenza si sta pagando (senza, l'API prende la prossima). */
  dueDate?: string | null;
  /** Solo PAYABLE: perché la rata si chiude qui e non su FiC. */
  localPaidNote?: string | null;
}

/** Che documento si sta cestinando: fattura emessa o fattura ricevuta. */
export type DocTrashKind = 'invoice' | 'payable';

export interface IgnoreDocsArgs {
  kind: DocTrashKind;
  /** Id dei DOCUMENTI (non delle rate). */
  ids: string[];
  reason: IgnoreReasonKey;
  note?: string | null;
  /** "2026/57 · Milana Roberta" per il toast. */
  label?: string;
}

const kindRestorePath = (kind: DocTrashKind) => (kind === 'payable' ? '/admin/payables/restore' : '/admin/invoices/restore');

export function useAdminActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
    // Le fatture di Contabilità cambiano stato quando l'ultima rata è incassata
    qc.invalidateQueries({ queryKey: ['invoices'] });
  };

  /**
   * "Segna incassata" su una rata: celebrazione + Annulla (unmark) per 6 secondi.
   *
   * L'incasso si registra SOLO qui: su FattureInCloud non si scrive mai (niente
   * `syncToFic` nel body, e lato API il default è false). La rata resta chiusa
   * anche dopo il sync FiC e compare in Banca › Da comunicare al commercialista finché FiC non la
   * recupera.
   */
  const markPaid = useMutation({
    mutationFn: ({ paymentId, paidDate, method, accountId, bankTransactionId, localPaidNote }: MarkPaidArgs) =>
      api.post(`/admin/incassi/${paymentId}/mark-paid`, {
        paidDate, method,
        accountId: accountId || undefined,
        bankTransactionId: bankTransactionId || undefined,
        localPaidNote: localPaidNote || undefined,
      }),
    onSuccess: (_r, vars) => {
      invalidate();
      celebrate({ title: 'Fattura incassata', amount: vars.amount, tone: 'ok' });
      toast.undo('Incasso registrato', {
        description: `${vars.label ? `${vars.label} · ` : ''}€ ${fmtEur(vars.amount)} il ${fmtDate(vars.paidDate)}`,
        onUndo: () => {
          api.post(`/admin/incassi/${vars.paymentId}/unmark`)
            .then(() => { invalidate(); vars.onUndone?.(); })
            .catch((e: any) => toast.alert('Incasso non annullato', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Incasso non registrato', e?.message ?? 'Riprova'),
  });

  /** Riporta una rata a "da incassare" (azione esplicita da Fatture già incassate). */
  const unmarkPaid = useMutation({
    mutationFn: ({ paymentId }: { paymentId: string; label?: string }) => api.post(`/admin/incassi/${paymentId}/unmark`),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Rata rimessa fra quelle da incassare', vars.label); },
    onError: (e: any) => toast.alert('Rata non rimessa fra quelle da incassare', e?.message ?? 'Riprova'),
  });

  /** "Segna pagata" su un'uscita (fornitore, spesa fissa, tassa o contanti): registra, non paga niente. */
  const pay = useMutation({
    mutationFn: ({ source, id, paidDate, method, accountId, bankTransactionId, dueDate, localPaidNote }: PayArgs) =>
      api.post('/admin/uscite/pay', {
        source, id, paidDate, method,
        dueDate: source === 'RECURRING' && dueDate ? dueDate : undefined,
        accountId: accountId || undefined,
        bankTransactionId: bankTransactionId || undefined,
        localPaidNote: localPaidNote || undefined,
      }),
    onSuccess: (_r, vars) => {
      invalidate();
      toast.ok('Pagamento registrato', `${vars.label ? `${vars.label} · ` : ''}€ ${fmtEur(vars.amount)} il ${fmtDate(vars.paidDate)}`);
    },
    onError: (e: any) => toast.alert('Pagamento non registrato', e?.message ?? 'Riprova'),
  });

  /**
   * Abbina un movimento Qonto a una rata / fattura ricevuta / rata tassa / voce
   * contanti. Con markPaid (default) la voce viene anche segnata incassata o
   * pagata alla data valuta del movimento: abbinare = registrare.
   */
  const match = useMutation({
    mutationFn: ({ transactionId, kind, id, markPaid }: { transactionId: string; kind: MatchKind; id: string; label?: string; markPaid?: boolean }) =>
      api.post(`/admin/transactions/${transactionId}/match`, { kind, id, markPaid: markPaid ?? true }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Movimento della banca abbinato', vars.label); },
    onError: (e: any) => toast.alert('Movimento della banca non abbinato', e?.message ?? 'Riprova'),
  });

  const unmatch = useMutation({
    mutationFn: ({ transactionId }: { transactionId: string; label?: string }) => api.delete(`/admin/transactions/${transactionId}/match`),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Movimento della banca scollegato', vars.label); },
    onError: (e: any) => toast.alert('Movimento della banca non scollegato', e?.message ?? 'Riprova'),
  });

  /** Aggiorna i saldi Qonto (conti + movimenti). Toast con i numeri di `stats` dell'API. */
  const syncQonto = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { accounts?: number; created?: number; updated?: number; taxMatched?: number } } | undefined>('/admin/sync/qonto'),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['qonto-status'] });
      const s = r?.stats;
      const bits: string[] = [];
      if (s?.created != null) bits.push(s.created === 1 ? '1 movimento nuovo' : `${s.created} movimenti nuovi`);
      if (s?.updated != null) bits.push(s.updated === 1 ? '1 movimento aggiornato' : `${s.updated} movimenti aggiornati`);
      if (s?.taxMatched) bits.push(s.taxMatched === 1 ? '1 F24 abbinato alla sua tassa' : `${s.taxMatched} F24 abbinati alle loro tasse`);
      toast.ok('Movimenti di Qonto aggiornati', bits.join(' · ') || undefined);
    },
    onError: (e: any) => toast.alert('Movimenti di Qonto non aggiornati', e?.message ?? 'Riprova'),
  });

  /** Import completo da FattureInCloud (~25 s): fatture, rate, ricevute, tasse. */
  const syncFic = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { invoices?: number; invoicePayments?: number; payables?: number; taxes?: number } } | undefined>('/admin/sync/fic'),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['fic-status'] });
      const s = r?.stats;
      const bits: string[] = [];
      if (s?.invoices != null) bits.push(s.invoices === 1 ? '1 fattura emessa' : `${s.invoices} fatture emesse`);
      if (s?.invoicePayments != null) bits.push(s.invoicePayments === 1 ? '1 rata' : `${s.invoicePayments} rate`);
      if (s?.payables != null) bits.push(s.payables === 1 ? '1 fattura di un fornitore' : `${s.payables} fatture dei fornitori`);
      toast.ok('Fatture di FattureInCloud aggiornate', bits.join(' · ') || undefined);
    },
    onError: (e: any) => toast.alert('Fatture di FattureInCloud non aggiornate', e?.message ?? 'Riprova'),
  });

  /** PATCH generico su una risorsa admin (tasse, ricorrenti, fatture ricevute, conti). */
  const patch = useMutation({
    mutationFn: ({ path, data }: { path: string; data: Record<string, unknown>; okTitle?: string }) => api.patch(path, data),
    onSuccess: (_r, vars) => { invalidate(); if (vars.okTitle) toast.ok(vars.okTitle); },
    onError: (e: any) => toast.alert('Modifica non salvata', e?.message ?? 'Riprova'),
  });

  /**
   * Toglie dall'elenco uno o più documenti (era "Cestina"): spariscono da liste
   * e totali, restano fra le fatture tolte dall'elenco (?status=cestinate) con
   * motivo e data. Non tocca FattureInCloud e non cancella niente dal database,
   * quindi l'Annulla del toast rimette le cose esattamente com'erano.
   */
  const ignoreDocs = useMutation({
    mutationFn: ({ kind, ids, reason, note }: IgnoreDocsArgs) =>
      api.post(kind === 'payable' ? '/admin/payables/ignore' : '/admin/invoices/ignore', { ids, reason, note: note || undefined }),
    onSuccess: (_r, vars) => {
      invalidate();
      const piu = vars.ids.length > 1;
      toast.undo(piu ? `${vars.ids.length} fatture tolte dall'elenco` : "Fattura tolta dall'elenco", {
        description: vars.label,
        onUndo: () => {
          api.post(kindRestorePath(vars.kind), { ids: vars.ids })
            .then(() => { invalidate(); toast.ok(piu ? "Fatture rimesse nell'elenco" : "Fattura rimessa nell'elenco"); })
            .catch((e: any) => toast.alert(piu ? "Fatture non rimesse nell'elenco" : "Fattura non rimessa nell'elenco", e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any, vars) => toast.alert(vars.ids.length > 1 ? "Fatture non tolte dall'elenco" : "Fattura non tolta dall'elenco", e?.message ?? 'Riprova'),
  });

  /** Rimette nell'elenco un documento tolto (tasto "Rimetti nell'elenco"). */
  const restoreDocs = useMutation({
    mutationFn: ({ kind, ids }: { kind: DocTrashKind; ids: string[]; label?: string }) =>
      api.post(kindRestorePath(kind), { ids }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok(vars.ids.length > 1 ? "Fatture rimesse nell'elenco" : "Fattura rimessa nell'elenco", vars.label); },
    onError: (e: any, vars) => toast.alert(vars.ids.length > 1 ? "Fatture non rimesse nell'elenco" : "Fattura non rimessa nell'elenco", e?.message ?? 'Riprova'),
  });

  // ── Riconciliazione banca ↔ documenti ───────────────────────────────────

  /**
   * Applica in automatico SOLO i casi certi e aggancia i movimenti delle rate
   * già pagate. Torna il prima e il dopo, e il numero del lotto con cui si
   * torna indietro: la riga "Annulla" del toast fa esattamente quello.
   */
  const applicaRiconciliazione = useMutation({
    mutationFn: () => api.post<{ lotto: string; chiuse: number; valore: number; agganciate: number; prima: { totaleAperto: number }; dopo: { totaleAperto: number } }>('/admin/riconciliazione/applica', {}),
    onSuccess: (r) => {
      invalidate();
      // Il titolo dice cosa è successo alle fatture, la riga sotto i soldi:
      // niente "rate chiuse" né "aperto da … a …", che da soli non si capiscono.
      const titolo = r.chiuse === 0
        ? 'Nessun abbinamento sicuro da confermare'
        : r.chiuse === 1 ? '1 rata di fattura segnata pagata con il suo movimento' : `${r.chiuse} rate di fatture segnate pagate con il loro movimento`;
      const righe = [
        r.chiuse ? `€ ${fmtEur(r.valore)} in tutto` : null,
        r.agganciate ? (r.agganciate === 1 ? '1 movimento collegato a una rata già pagata' : `${r.agganciate} movimenti collegati a rate già pagate`) : null,
        r.chiuse ? `fatture ancora da incassare o da pagare: da € ${fmtEur(r.prima.totaleAperto)} a € ${fmtEur(r.dopo.totaleAperto)}` : null,
      ].filter(Boolean);
      toast.undo(titolo, {
        description: righe.join(' · ') || undefined,
        onUndo: () => {
          api.post('/admin/riconciliazione/annulla', { lotto: r.lotto })
            .then(() => { invalidate(); toast.ok('Abbinamenti annullati', "Le rate sono tornate com'erano prima"); })
            .catch((e: any) => toast.alert('Abbinamenti non annullati', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Abbinamenti sicuri non confermati', e?.message ?? 'Riprova'),
  });

  /**
   * Conferma una singola proposta: si comporta come il pulsante "Segna incassata" /
   * "Segna pagata". `nota` dice da dove è stata confermata (finisce fra i pagamenti da
   * comunicare al commercialista); senza, l'API mette la sua.
   */
  const confermaProposta = useMutation({
    mutationFn: (v: { lato: 'INCASSO' | 'USCITA'; rataId: string; txId: string; paidDate: string; label?: string; importo?: number; nota?: string }) =>
      api.post('/admin/riconciliazione/conferma', { lato: v.lato, rataId: v.rataId, txId: v.txId, paidDate: v.paidDate, ...(v.nota ? { nota: v.nota } : {}) }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.ok(v.lato === 'INCASSO' ? 'Incasso registrato' : 'Pagamento registrato', `${v.label ?? ''}${v.importo != null ? ` · € ${fmtEur(v.importo)}` : ''}`);
    },
    onError: (e: any, v) => toast.alert(v.lato === 'INCASSO' ? 'Incasso non registrato' : 'Pagamento non registrato', e?.message ?? 'Riprova'),
  });

  /** "Questa proposta è sbagliata": la coppia rata+movimento non si ripropone più. */
  const scartaProposta = useMutation({
    mutationFn: (v: { rataId: string; txId: string; label?: string }) => api.post('/admin/riconciliazione/scarta', { rataId: v.rataId, txId: v.txId }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.undo('Abbinamento proposto scartato', {
        description: v.label,
        onUndo: () => {
          api.post('/admin/riconciliazione/ripristina-scarto', { rataId: v.rataId, txId: v.txId })
            .then(() => invalidate())
            .catch((e: any) => toast.alert('Abbinamento proposto non rimesso in lista', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Abbinamento proposto non scartato', e?.message ?? 'Riprova'),
  });

  const ripristinaScarto = useMutation({
    mutationFn: (v: { rataId: string; txId: string }) => api.post('/admin/riconciliazione/ripristina-scarto', { rataId: v.rataId, txId: v.txId }),
    onSuccess: () => { invalidate(); toast.ok('Abbinamento proposto rimesso in lista'); },
    onError: (e: any) => toast.alert('Abbinamento proposto non rimesso in lista', e?.message ?? 'Riprova'),
  });

  /** Crea le uscite mancanti di una voce ricorrente (le rate vere delle auto). */
  const creaRateMancanti = useMutation({
    mutationFn: async (v: { recurringId: string; nome: string; addebiti: number; totale: number }) => {
      const ok = await confirmDialog({
        title: `Aggiungere le ${v.addebiti} rate di ${v.nome} che mancano?`,
        body: `In banca ci sono ${v.addebiti} addebiti veri, per € ${fmtEur(v.totale)}, che qui non risultano da nessuna parte. Diventano rate già pagate, con la data e l'importo di ogni addebito. Si può annullare.`,
        confirmLabel: 'Aggiungi le rate',
        icon: 'repeat',
      });
      if (!ok) return null;
      return api.post<{ create: number; totale: number; lotto: string }>('/admin/riconciliazione/rate-mancanti/crea', { recurringId: v.recurringId });
    },
    onSuccess: (r, v) => {
      if (!r) return;
      invalidate();
      toast.undo(r.create === 1 ? `1 rata di ${v.nome} aggiunta` : `${r.create} rate di ${v.nome} aggiunte`, {
        description: `€ ${fmtEur(r.totale)} in tutto, già pagate`,
        onUndo: () => {
          api.post('/admin/riconciliazione/rate-mancanti/annulla', { lotto: r.lotto })
            .then(() => { invalidate(); toast.ok(`Rate di ${v.nome} tolte di nuovo`); })
            .catch((e: any) => toast.alert(`Rate di ${v.nome} non tolte`, e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any, v) => toast.alert(`Rate di ${v.nome} non aggiunte`, e?.message ?? 'Riprova'),
  });

  /** Annulla un lotto di riconciliazione già eseguito (dalla lista in fondo alla tab). */
  const annullaLotto = useMutation({
    mutationFn: async (v: { lotto: string; chiuse: number }) => {
      const ok = await confirmDialog({
        title: 'Annullare questi abbinamenti?',
        body: `${v.chiuse === 1 ? '1 rata torna com\'era prima' : `${v.chiuse} rate tornano come erano prima`}, con la loro data e il loro conto, e i movimenti della banca si staccano dalle fatture.`,
        confirmLabel: 'Annulla gli abbinamenti',
        icon: 'undo',
        danger: true,
      });
      if (!ok) return null;
      return api.post('/admin/riconciliazione/annulla', { lotto: v.lotto });
    },
    onSuccess: (r) => { if (!r) return; invalidate(); toast.ok('Abbinamenti annullati'); },
    onError: (e: any) => toast.alert('Abbinamenti non annullati', e?.message ?? 'Riprova'),
  });

  /** DELETE con conferma. Ritorna null se l'utente annulla. */
  const remove = useMutation({
    mutationFn: async ({ path, title, body }: { path: string; title: string; body?: string; okTitle?: string }) => {
      const ok = await confirmDialog({ title, body, danger: true, confirmLabel: 'Elimina', icon: 'trash' });
      if (!ok) return null;
      await api.delete(path);
      return true;
    },
    onSuccess: (done, vars) => { if (!done) return; invalidate(); toast.ok(vars.okTitle ?? 'Voce eliminata'); },
    onError: (e: any) => toast.alert('Eliminazione non riuscita', e?.message ?? 'Riprova'),
  });

  return {
    markPaid, unmarkPaid, pay, match, unmatch, syncQonto, syncFic, patch, remove, ignoreDocs, restoreDocs, invalidate,
    applicaRiconciliazione, confermaProposta, scartaProposta, ripristinaScarto, creaRateMancanti, annullaLotto,
  };
}
